import * as React from "react"
import { useNavigate } from "react-router-dom"
import {
  Search,
  CornerDownLeft,
  ArrowUp,
  ArrowDown,
  Loader2,
  Newspaper,
  Layout,
  Network,
} from "lucide-react"
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command"
import { Button } from "@/components/ui/button"
import { useSearchData } from "@/hooks/useSearchData"
import {
  buildNavigationFlags,
  filterNavigation,
  isNavigationChildGroup,
  isNavigationChildLink,
  navigationItems as navigationRegistry,
  type NavigationFlags,
} from "@/layouts/navigation"
import { useWorkspaceStore } from "@/stores/useWorkspaceStore"
import { universalBlogEditorPath } from "@/utils/editorRoutes"

type CommandNavEntry = { title: string; url: string; icon: React.ElementType }
type CommandNavGroup = { group: string; items: CommandNavEntry[] }

// Derive the static command-palette entries from the sidebar nav registry,
// applying the same per-item gates the sidebar uses. Top-level leaf links
// (Home, AI Chat) form a "Navigation" group; each parent becomes a group
// named by its title with parent→group→link children flattened.
function buildCommandNavGroups(flags: NavigationFlags): CommandNavGroup[] {
  const visibleItems = filterNavigation(navigationRegistry, flags)
  const topLevelLinks: CommandNavEntry[] = []
  const parentGroups: CommandNavGroup[] = []

  for (const item of visibleItems) {
    if (!item.items) {
      topLevelLinks.push({ title: item.title, url: item.url, icon: item.icon })
      continue
    }

    const links = item.items
      .flatMap((child) => (isNavigationChildGroup(child) ? child.items : [child]))
      .filter(isNavigationChildLink)
      .map((link) => ({ title: link.title, url: link.url, icon: link.icon ?? item.icon }))

    if (links.length) parentGroups.push({ group: item.title, items: links })
  }

  return [
    ...(topLevelLinks.length ? [{ group: "Navigation", items: topLevelLinks }] : []),
    ...parentGroups,
  ]
}

// Status badge colors
const getStatusColor = (status: string) => {
  switch (status) {
    case 'completed':
    case 'published':
      return 'text-green-600 dark:text-green-400'
    case 'failed':
      return 'text-red-600 dark:text-red-400'
    case 'pending':
    case 'new':
      return 'text-muted-foreground'
    default:
      return 'text-blue-600 dark:text-blue-400'
  }
}

export function SearchCommand() {
  const [open, setOpen] = React.useState(false)
  const navigate = useNavigate()
  const { templates, blogPosts, topicalMaps, isLoading } = useSearchData(open)
  const { userProfile, currentWorkspace } = useWorkspaceStore()
  const navigationItems = React.useMemo(
    () => buildCommandNavGroups(buildNavigationFlags(userProfile, currentWorkspace)),
    [userProfile, currentWorkspace],
  )

  React.useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setOpen((open) => !open)
      }
    }

    document.addEventListener("keydown", down)
    return () => document.removeEventListener("keydown", down)
  }, [])

  const runCommand = React.useCallback((command: () => void) => {
    setOpen(false)
    command()
  }, [])

  // Show only top 5 results for each dynamic category
  const displayTemplates = templates.slice(0, 5)
  const displayBlogPosts = blogPosts.slice(0, 5)
  const displayTopicalMaps = topicalMaps.slice(0, 5)

  const hasDynamicContent = templates.length > 0 || blogPosts.length > 0 || topicalMaps.length > 0

  return (
    <>
      <Button
        variant="outline"
        className="relative h-9 w-full justify-start rounded-md bg-muted/30 hover:bg-muted/50 border-border/50 hover:border-border transition-colors text-sm font-normal text-muted-foreground shadow-none sm:pr-12 md:w-40 lg:w-64"
        onClick={() => setOpen(true)}
      >
        <Search className="mr-2 h-4 w-4" />
        <span className="hidden lg:inline-flex">Search...</span>
        <span className="inline-flex lg:hidden">Search...</span>
        <kbd className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 hidden h-5 select-none items-center gap-1 rounded border border-border/50 bg-background px-1.5 font-mono text-[10px] font-medium text-muted-foreground/70 sm:flex">
          <span className="text-xs">⌘</span>K
        </kbd>
      </Button>
      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput placeholder="Search pages, templates, blog posts..." />
        <CommandList>
          <CommandEmpty>No results found.</CommandEmpty>

          {/* Static Navigation Items */}
          {navigationItems.map((group, index) => (
            <React.Fragment key={group.group}>
              <CommandGroup heading={group.group}>
                {group.items.map((item) => (
                  <CommandItem
                    key={item.url}
                    value={`${group.group} ${item.title}`}
                    onSelect={() => runCommand(() => navigate(item.url))}
                  >
                    <item.icon className="mr-2 h-4 w-4 text-muted-foreground" />
                    <span>{item.title}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
              {(index < navigationItems.length - 1 || hasDynamicContent) && <CommandSeparator />}
            </React.Fragment>
          ))}

          {/* Loading State */}
          {isLoading && (
            <div className="py-6 text-center text-sm text-muted-foreground">
              <Loader2 className="mx-auto h-5 w-5 animate-spin mb-2" />
              Loading content...
            </div>
          )}

          {/* Blog Templates */}
          {displayTemplates.length > 0 && (
            <>
              <CommandGroup heading="Blog Templates">
                {displayTemplates.map((template) => (
                  <CommandItem
                    key={template.id}
                    value={`template ${template.name} ${template.slug}`}
                    onSelect={() => runCommand(() => navigate(`/content/templates?template=${template.id}`))}
                  >
                    <Layout className="mr-2 h-4 w-4 text-coral" />
                    <div className="flex flex-col">
                      <span className="flex items-center gap-2">
                        {template.icon && <span>{template.icon}</span>}
                        {template.name}
                        {template.is_system && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                            System
                          </span>
                        )}
                      </span>
                      {template.description && (
                        <span className="text-xs text-muted-foreground truncate max-w-[300px]">
                          {template.description}
                        </span>
                      )}
                    </div>
                  </CommandItem>
                ))}
                {templates.length > 5 && (
                  <CommandItem
                    value="view all templates"
                    onSelect={() => runCommand(() => navigate('/content/templates'))}
                  >
                    <span className="text-muted-foreground text-sm">
                      View all {templates.length} templates...
                    </span>
                  </CommandItem>
                )}
              </CommandGroup>
              <CommandSeparator />
            </>
          )}

          {/* Blog Posts */}
          {displayBlogPosts.length > 0 && (
            <>
              <CommandGroup heading="Blog Posts">
                {displayBlogPosts.map((post) => (
                  <CommandItem
                    key={post.id}
                    value={`blog post ${post.title} ${post.keyword}`}
                    onSelect={() => runCommand(() => navigate(universalBlogEditorPath(post.id)))}
                  >
                    <Newspaper className="mr-2 h-4 w-4 text-blue-500 dark:text-blue-400" />
                    <div className="flex flex-col flex-1 min-w-0">
                      <span className="truncate">{post.title}</span>
                      <span className="text-xs text-muted-foreground flex items-center gap-2">
                        <span className="truncate">{post.keyword}</span>
                        <span className={`${getStatusColor(post.status)} capitalize`}>
                          {post.status.replace('_', ' ')}
                        </span>
                      </span>
                    </div>
                  </CommandItem>
                ))}
                {blogPosts.length > 5 && (
                  <CommandItem
                    value="view all blog posts"
                    onSelect={() => runCommand(() => navigate('/content/planner'))}
                  >
                    <span className="text-muted-foreground text-sm">
                      View all {blogPosts.length} blog posts...
                    </span>
                  </CommandItem>
                )}
              </CommandGroup>
              <CommandSeparator />
            </>
          )}

          {/* Topical Maps */}
          {displayTopicalMaps.length > 0 && (
            <CommandGroup heading="Content Planner">
              {displayTopicalMaps.map((map) => (
                <CommandItem
                  key={map.id}
                  value={`topical map planner ${map.seed_keyword}`}
                  onSelect={() => runCommand(() => navigate(`/content/planner?map=${map.id}`))}
                >
                  <Network className="mr-2 h-4 w-4 text-green-500 dark:text-green-400" />
                  <div className="flex flex-col flex-1 min-w-0">
                    <span className="truncate">{map.seed_keyword}</span>
                    <span className="text-xs text-muted-foreground flex items-center gap-2">
                      <span>{map.total_clusters} clusters</span>
                      <span className={`${getStatusColor(map.status)} capitalize`}>
                        {map.status.replace('_', ' ')}
                      </span>
                    </span>
                  </div>
                </CommandItem>
              ))}
              {topicalMaps.length > 5 && (
                <CommandItem
                  value="view all topical maps"
                  onSelect={() => runCommand(() => navigate('/content/planner'))}
                >
                  <span className="text-muted-foreground text-sm">
                    View all {topicalMaps.length} topical maps...
                  </span>
                </CommandItem>
              )}
            </CommandGroup>
          )}
        </CommandList>

        {/* Footer with keyboard hints */}
        <div className="flex items-center justify-between border-t px-3 py-2 text-xs text-muted-foreground">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1">
              <kbd className="inline-flex h-5 items-center justify-center rounded border bg-muted px-1.5 font-mono text-[10px]">
                <ArrowUp className="h-3 w-3" />
              </kbd>
              <kbd className="inline-flex h-5 items-center justify-center rounded border bg-muted px-1.5 font-mono text-[10px]">
                <ArrowDown className="h-3 w-3" />
              </kbd>
              <span className="ml-1">Navigate</span>
            </div>
            <div className="flex items-center gap-1">
              <kbd className="inline-flex h-5 items-center justify-center rounded border bg-muted px-1.5 font-mono text-[10px]">
                <CornerDownLeft className="h-3 w-3" />
              </kbd>
              <span className="ml-1">Select</span>
            </div>
            <div className="flex items-center gap-1">
              <kbd className="inline-flex h-5 items-center justify-center rounded border bg-muted px-1.5 font-mono text-[10px]">
                Esc
              </kbd>
              <span className="ml-1">Close</span>
            </div>
          </div>
        </div>
      </CommandDialog>
    </>
  )
}
